<template>
  <div class="inventory-ticker-host">
    <transition name="ticker-slide">
      <aside
        v-if="visible"
        ref="bar"
        class="inventory-ticker steel-accent"
        role="region"
        aria-label="Trailers available for immediate sale"
      >
        <!-- "Available now" badge with a live pulse dot -->
        <div class="ticker-badge">
          <span class="ticker-dot" aria-hidden="true"></span>
          <span class="ticker-badge-text">Available Stock</span>
        </div>

        <!-- Scrolling marquee -->
        <div ref="viewport" class="ticker-viewport">
          <!-- Outer element glides the content in from the right one time... -->
          <div
            class="ticker-shift"
            :class="{ 'is-running': running }"
            :style="{ '--intro-duration': introSeconds + 's' }"
          >
            <!-- ...inner track loops seamlessly (content repeated to fill width) -->
            <div
              ref="track"
              class="ticker-track"
              :style="{ '--loop-duration': loopDuration + 's' }"
            >
              <span
                v-for="(item, i) in loopItems"
                :key="i"
                class="ticker-item"
              >
                <component
                  :is="entryTag(item)"
                  v-bind="entryAttrs(item)"
                  :class="['ticker-text', { 'is-link': isClickable(item) }]"
                  @click="onEntryClick(item, $event)"
                >{{ item.text }}</component>
                <span class="ticker-sep" aria-hidden="true">◆</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Dismiss -->
        <button
          class="ticker-close"
          type="button"
          aria-label="Dismiss availability ticker"
          @click="dismiss"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </aside>
    </transition>
  </div>
</template>

<script>
import { inventoryTicker } from '../data/inventoryTicker.js'
import { hasStockDetail, openStockViewer } from '../utils/stockViewer.js'

const DISMISS_COOKIE = 'ste_ticker_dismissed'
const DISMISS_DAYS = 7 // how long a dismissal sticks (until then, or until data changes)
const SCROLL_SPEED = 60 // pixels per second for the continuous loop
const INTRO_SECONDS = 2.6 // one-time glide-in from the right edge

// Small stable hash so we can tell when the ticker data has changed.
function hashContent(str) {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i)
    h |= 0
  }
  return (h >>> 0).toString(36)
}

export default {
  name: 'InventoryTicker',
  data() {
    return {
      visible: false,
      running: false, // animation only starts AFTER measuring is done
      items: inventoryTicker.items || [],
      loopDuration: 25,
      introSeconds: INTRO_SECONDS,
      // How many times to repeat the item list across the whole track. Must be
      // even so the -50% loop splits into two identical halves. Computed at
      // mount so each half overflows the viewport (otherwise short content
      // leaves a gap and the loop visibly jumps at the reset point).
      repeats: 2,
      resizeObserver: null,
    }
  },
  computed: {
    // The active config serialized, used to detect "data changed".
    contentVersion() {
      return hashContent(
        JSON.stringify({ a: inventoryTicker.active, i: this.items })
      )
    },
    // The item list repeated `repeats` times to build a seamless loop.
    loopItems() {
      const out = []
      for (let r = 0; r < this.repeats; r++) {
        out.push(...this.items)
      }
      return out
    },
  },
  mounted() {
    // Gate: only show if inventory is active and there's something to show.
    if (!inventoryTicker.active || this.items.length === 0) {
      return
    }
    // Stay hidden if dismissed and the data hasn't changed since.
    if (this.getDismissedVersion() === this.contentVersion) {
      return
    }
    this.visible = true
    // 1) Render once so we can measure the headline, 2) repeat it enough to
    // overflow the viewport and set the loop duration, 3) only THEN start the
    // animation. Doing all the measuring before the animation begins is what
    // keeps the strip from jumping/blinking into view.
    this.$nextTick(() => {
      this.expandToFill()
      this.$nextTick(() => {
        this.running = true
        this.updateOffset()
        this.observeSize()
      })
    })
  },
  beforeUnmount() {
    this.clearOffset()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  methods: {
    // --- Ticker entry click behaviour -------------------------------------
    // An entry opens the detail popup (TickerDetailModal, mounted in App.vue)
    // if it has `detail` or `images`; this takes priority over a plain `link`.
    hasPopup(item) {
      return hasStockDetail(item)
    },
    isClickable(item) {
      return this.hasPopup(item) || !!item.link
    },
    entryTag(item) {
      if (this.hasPopup(item)) return 'button'
      if (item.link) return 'a'
      return 'span'
    },
    entryAttrs(item) {
      if (this.hasPopup(item)) return { type: 'button' }
      if (item.link) return { href: item.link }
      return {}
    },
    onEntryClick(item, e) {
      if (this.hasPopup(item)) {
        e.preventDefault()
        openStockViewer(item)
      }
      // Otherwise it's a plain link/span — let the default behaviour run.
    },

    // Repeat the content until each half of the track overflows the viewport
    // (so the seamless loop has no gap), and set the loop duration from the
    // real width so the scroll speed is consistent regardless of how much text
    // there is. Runs once, before the animation starts.
    expandToFill() {
      const track = this.$refs.track
      const viewport = this.$refs.viewport
      if (!track || !viewport) return
      // Width of a single pass of the item list (track currently has `repeats`).
      const oneSetWidth = track.scrollWidth / this.repeats
      if (oneSetWidth <= 0) return
      const viewportWidth = viewport.clientWidth
      // Enough sets so one half is wider than the viewport, plus a spare set of
      // buffer to stay seamless through minor responsive width changes.
      const setsPerHalf = Math.max(1, Math.ceil(viewportWidth / oneSetWidth) + 1)
      this.repeats = setsPerHalf * 2
      const halfWidth = setsPerHalf * oneSetWidth
      this.loopDuration = Math.max(10, Math.round(halfWidth / SCROLL_SPEED))
    },
    // Publish the bar's height so fixed page elements (the up/down nav arrows)
    // can shift up by exactly this much while the ticker is open. Only touches a
    // CSS variable — never the animation — so it can't reintroduce the jump.
    observeSize() {
      this.resizeObserver = new ResizeObserver(() => this.updateOffset())
      if (this.$refs.bar) {
        this.resizeObserver.observe(this.$refs.bar)
      }
    },
    updateOffset() {
      if (this.$refs.bar) {
        document.documentElement.style.setProperty(
          '--ticker-offset',
          this.$refs.bar.offsetHeight + 'px'
        )
      }
    },
    clearOffset() {
      document.documentElement.style.setProperty('--ticker-offset', '0px')
    },
    getDismissedVersion() {
      const match = document.cookie
        .split('; ')
        .find((c) => c.startsWith(DISMISS_COOKIE + '='))
      return match ? decodeURIComponent(match.split('=')[1]) : null
    },
    dismiss() {
      const expires = new Date()
      expires.setDate(expires.getDate() + DISMISS_DAYS)
      document.cookie =
        `${DISMISS_COOKIE}=${encodeURIComponent(this.contentVersion)};` +
        ` expires=${expires.toUTCString()}; path=/; SameSite=Lax`
      // Drop the offset now so the arrows glide back down as the bar slides out.
      this.clearOffset()
      this.visible = false
    },
  },
}
</script>

<style scoped>
.inventory-ticker {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9000; /* below the cookie banner (9999) so that floats on top */
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 2.75rem;
  padding: 0 0.75rem 0 1rem;
  overflow: hidden;
  background: rgba(29, 30, 38, 0.94);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    0 -4px 24px rgba(0, 0, 0, 0.45),
    0 0 50px rgba(143, 0, 0, 0.06);
}

/* "Available Now" badge */
.ticker-badge {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
  padding-right: 1rem;
  border-right: 1px solid rgba(183, 185, 186, 0.18);
}
.ticker-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff3b3b;
  box-shadow: 0 0 8px rgba(255, 59, 59, 0.8);
  animation: ticker-pulse 2s ease-in-out infinite;
}
.ticker-badge-text {
  font-family: 'PT Sans', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(183, 185, 186, 0.95);
  white-space: nowrap;
}

/* Marquee */
.ticker-viewport {
  position: relative;
  flex: 1;
  overflow: hidden;
  /* Soft fade at both edges */
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent 0,
    #000 2.5rem,
    #000 calc(100% - 2.5rem),
    transparent 100%
  );
  mask-image: linear-gradient(
    90deg,
    transparent 0,
    #000 2.5rem,
    #000 calc(100% - 2.5rem),
    transparent 100%
  );
}

/* Outer wrapper: holds the content off-screen to the right, then glides it in
   one time. Width = viewport, so translateX(100%) parks content just off the
   right edge for a clean entrance (no pop). */
.ticker-shift {
  display: block;
  width: 100%;
  transform: translateX(100%);
  will-change: transform;
}
.ticker-shift.is-running {
  animation: ticker-intro var(--intro-duration, 2.6s)
    cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

/* Inner track: the seamless, infinite loop */
.ticker-track {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  will-change: transform;
}
.ticker-shift.is-running .ticker-track {
  animation: ticker-scroll var(--loop-duration, 25s) linear infinite;
}
.ticker-viewport:hover .ticker-track {
  animation-play-state: paused;
}

.ticker-item {
  display: inline-flex;
  align-items: center;
}
.ticker-text {
  font-family: 'PT Sans', sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  color: #e8e9ea;
  text-decoration: none;
  /* reset so <button> entries match the text/link ones */
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  line-height: inherit;
  cursor: default;
  -webkit-appearance: none;
  appearance: none;
}
.ticker-text.is-link {
  color: #f0f0f0;
  cursor: pointer;
  transition: color 0.25s ease;
}
.ticker-text.is-link:hover {
  color: #ff6a6a;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.ticker-sep {
  margin: 0 1.5rem;
  font-size: 0.55rem;
  color: rgba(143, 0, 0, 0.9);
  transform: translateY(-1px);
}

/* Dismiss button */
.ticker-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  font-size: 1.35rem;
  line-height: 1;
  color: rgba(183, 185, 186, 0.7);
  background: transparent;
  border: 1px solid rgba(183, 185, 186, 0.18);
  border-radius: 0.35rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.25, 1, 0.5, 1);
}
.ticker-close:hover {
  color: #f0f0f0;
  background: linear-gradient(135deg, #8f0000, #a31a1a);
  border-color: transparent;
}

@keyframes ticker-intro {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
@keyframes ticker-scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
@keyframes ticker-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.85); }
}

/* Slide-up entrance/exit (mirrors the cookie banner) */
.ticker-slide-enter-active {
  transition: all 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.ticker-slide-leave-active {
  transition: all 0.35s cubic-bezier(0.55, 0, 1, 0.45);
}
.ticker-slide-enter-from,
.ticker-slide-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

@media (max-width: 640px) {
  .inventory-ticker {
    height: 2.5rem;
    gap: 0.6rem;
    padding: 0 0.4rem 0 0.6rem;
  }
  .ticker-badge {
    padding-right: 0.6rem;
  }
  .ticker-badge-text {
    /* Keep the live dot, drop the label to save room on small screens */
    display: none;
  }
  .ticker-text {
    font-size: 0.8rem;
  }
  .ticker-sep {
    margin: 0 1rem;
  }
}

/* Respect users who prefer no motion: hold the content still and let it scroll
   manually. (Without this the content would stay parked off-screen.) */
@media (prefers-reduced-motion: reduce) {
  .ticker-shift,
  .ticker-shift.is-running {
    transform: none;
    animation: none;
  }
  .ticker-shift.is-running .ticker-track {
    animation: none;
  }
  .ticker-viewport {
    overflow-x: auto;
  }
}
</style>
